import React, { useState, useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { theme, alpha } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { useNotification } from './Notification';
import { useGroupDetail } from '../hooks/useGroups';
import { getApiUrl } from '../config';
import Avatar from './ui/Avatar';
import { useAuth } from '../contexts/AuthContext';
import { PlayerPickerModal } from './h2h/PlayerPickerModal';
import { GroupLeaderboard } from './group/GroupLeaderboard';
import { AddMatchesFromHistoryModal } from './group/AddMatchesFromHistoryModal';
import { PollCard, type PollData } from './group/PollCard';
import { CreatePollModal } from './group/CreatePollModal';
import { GroupChat } from './group/GroupChat';
import SeasonStandings from './group/SeasonStandings';
import CreateRecurringMatchModal from './CreateRecurringMatchModal';

interface SearchUser {
  id: string;
  name: string;
  avatar?: string;
  rating: number;
}

interface GroupAnnouncement {
  id: string;
  message: string;
  createdAt: string;
  sender: { id: string; name: string; avatar?: string | null };
  reactions?: Record<string, number>;
  userReacted?: boolean;
  readCount?: number;
  totalMembers?: number;
}

interface GroupStatsResponse {
  groupInfo: { createdAt?: string; totalMembers: number; totalMatches: number; upcomingMatches: number };
  topAttendance: Array<{ id: string; name: string; attendance: number; playedMatches: number }>;
  topScorers: Array<{ id: string; name: string; totalGoals: number }>;
  topStreaks: Array<{ id: string; name: string; currentStreak: number }>;
  mvp?: { id: string; name: string; rating: number } | null;
}

interface GroupMatchItem {
  id: string;
  location: string;
  date: string;
  time: string;
  status: string;
  playersJoined: number;
  maxPlayers: number;
  team1Name?: string;
  team2Name?: string;
  team1Goals?: number | null;
  team2Goals?: number | null;
  recurringInstance?: { id: string; recurringMatchId: string } | null;
  userPlayer?: { teamId?: number | null; position?: string | null } | null;
}

interface RecurringMatchSeries {
  id: string;
  name?: string | null;
  dayOfWeek: number;
  time: string;
  isActive: boolean;
  pausedAt?: string | null;
  pausedReason?: string | null;
  frequency?: 'WEEKLY' | 'BIWEEKLY';
  consecutiveLowTurnout?: number;
  matchesSinceConfirmation?: number;
  upcomingMatchesCount?: number;
  upcomingMatches?: Array<{
    id: string;
    date: string;
    time: string;
    playerCount: number;
    maxPlayers: number;
  }>;
  templateMatch: { location: string; price: number | string; maxPlayers?: number };
}

interface GroupPendingMember {
  id: string;
  guestName: string;
  guestPosition: string;
  guestRating: number;
  totalMatches: number;
  totalGoals: number;
  totalAssists: number;
  status: 'PENDING' | 'CLAIMED';
}

interface GroupJoinRequest {
  id: string;
  createdAt: string;
  user: { id: string; name: string; avatar?: string | null };
}

type GroupTab = 'members' | 'activity' | 'polls' | 'chat' | 'ranking' | 'seasons' | 'info';

const GroupDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightAnnouncementId = searchParams.get('announcement');
  const initialTabParam = searchParams.get('tab');
  const { isMobile } = useResponsive();
  const { showNotification } = useNotification();
  const { user: authUser } = useAuth();
  const { group, loading, refetch } = useGroupDetail(id, { onError: (msg) => showNotification(msg, 'error') });

  // Tiempo real: reaccionar a eventos del grupo (borrado, edición, rol, miembro
  // removido, encuesta/anuncio). El handler se actualiza en cada render (abajo).
  const groupEventHandlerRef = useRef<(data: any) => void>(() => {});
  const [chatUnread, setChatUnread] = useState(0);
  const activeTabRef = useRef<GroupTab>('members');
  useEffect(() => {
    if (!id || !authUser?.id) return;
    const socketUrl = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
    const s: Socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      forceNew: true,
      auth: { token: localStorage.getItem('authToken') },
    });
    s.on('connect', () => s.emit('joinGroup', id));
    s.on('groupEvent', (data: any) => groupEventHandlerRef.current(data));
    // Mensaje de chat nuevo: subir el badge si no estoy mirando el chat.
    s.on('groupMessage', (evt: any) => {
      const m = evt?.message;
      if (!m || m.groupId !== id) return;
      if (m.sender?.id === authUser?.id) return;
      if (activeTabRef.current !== 'chat') setChatUnread((c) => c + 1);
    });
    return () => {
      try { s.emit('leaveGroup', id); } catch {}
      try { s.disconnect(); } catch {}
    };
  }, [id, authUser?.id]);

  const [activeTab, setActiveTab] = useState<GroupTab>('members');
  activeTabRef.current = activeTab;

  // No-leídos del chat para el badge del tab. Al entrar al chat se limpia
  // (el componente de chat marca leído); al salir se vuelve a consultar.
  useEffect(() => {
    if (!id) return;
    if (activeTab === 'chat') { setChatUnread(0); return; }
    (async () => {
      try {
        const res = await fetch(getApiUrl(`/api/groups/${id}/unread-count`), {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        });
        const data = await res.json().catch(() => ({ count: 0 }));
        setChatUnread(data.count || 0);
      } catch {
        // no crítico
      }
    })();
  }, [id, activeTab]);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [addMembersQuery, setAddMembersQuery] = useState('');
  const [addMembersResults, setAddMembersResults] = useState<SearchUser[]>([]);
  const [addMembersSearching, setAddMembersSearching] = useState(false);
  const [addMembersSelected, setAddMembersSelected] = useState<Set<string>>(new Set());
  const [addMembersSubmitting, setAddMembersSubmitting] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showAddMatchesModal, setShowAddMatchesModal] = useState(false);
  const [quickJoiningId, setQuickJoiningId] = useState<string | null>(null);
  const [polls, setPolls] = useState<PollData[]>([]);
  const [pollsLoading, setPollsLoading] = useState(false);
  const [showCreatePollModal, setShowCreatePollModal] = useState(false);
  const [votingPollId, setVotingPollId] = useState<string | null>(null);
  const [groupNotifEnabled, setGroupNotifEnabled] = useState(true);
  const [groupNotifSaving, setGroupNotifSaving] = useState(false);
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [announcementSending, setAnnouncementSending] = useState(false);
  const [announcements, setAnnouncements] = useState<GroupAnnouncement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [announcementsError, setAnnouncementsError] = useState('');
  const [groupStats, setGroupStats] = useState<GroupStatsResponse | null>(null);
  const [groupStatsLoading, setGroupStatsLoading] = useState(false);
  const [myGroupStats, setMyGroupStats] = useState<{
    attendancePercentage: number;
    currentStreak: number;
    bestStreak: number;
    matchesAttended: number;
    totalMatches: number;
    isAboveAverage: boolean;
    matchHistory: Array<{ matchId: string; attended: boolean }>;
  } | null>(null);
  const [myGroupStatsLoading, setMyGroupStatsLoading] = useState(false);
  const [upcomingMatches, setUpcomingMatches] = useState<GroupMatchItem[]>([]);
  const [pastMatches, setPastMatches] = useState<GroupMatchItem[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [memberActionLoadingId, setMemberActionLoadingId] = useState<string | null>(null);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDescription, setEditGroupDescription] = useState('');
  const [editGroupSubmitting, setEditGroupSubmitting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkGenerating, setLinkGenerating] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [recurringMatches, setRecurringMatches] = useState<RecurringMatchSeries[]>([]);
  const [h2hPickerOpen, setH2hPickerOpen] = useState(false);
  const [recurringMatchLoading, setRecurringMatchLoading] = useState(false);
  const [pausingRecurringId, setPausingRecurringId] = useState<string | null>(null);
  const [confirmingRecurringId, setConfirmingRecurringId] = useState<string | null>(null);
  const [deletingRecurringId, setDeletingRecurringId] = useState<string | null>(null);
  const [creatingNextRecurringId, setCreatingNextRecurringId] = useState<string | null>(null);
  const [showCreateRecurringModal, setShowCreateRecurringModal] = useState(false);
  const [pendingMembers, setPendingMembers] = useState<GroupPendingMember[]>([]);
  const [pendingMembersLoading, setPendingMembersLoading] = useState(false);
  const [joinRequests, setJoinRequests] = useState<GroupJoinRequest[]>([]);
  const [joinRequestsLoading, setJoinRequestsLoading] = useState(false);
  const [processingJoinReqId, setProcessingJoinReqId] = useState<string | null>(null);
  const [showAddPendingModal, setShowAddPendingModal] = useState(false);
  const [pendingForm, setPendingForm] = useState({ guestName: '', guestPosition: 'Mediocampista', guestRating: 3 });
  const [pendingFormSubmitting, setPendingFormSubmitting] = useState(false);
  const recurringFetchedRef = useRef(false);

  const memberIds = group?.members.map((m) => m.id) ?? [];
  const searchUsersFiltered = addMembersResults.filter((u) => !memberIds.includes(u.id));

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json',
  });

  const fetchPendingMembers = useCallback(async () => {
    if (!id) return;
    setPendingMembersLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/pending-members`), { headers: authHeaders() });
      const data = await res.json().catch(() => []);
      setPendingMembers(Array.isArray(data) ? data : []);
    } catch {
      // non-critical, don't show error
    } finally {
      setPendingMembersLoading(false);
    }
  }, [id]);

  const fetchJoinRequests = useCallback(async () => {
    if (!id) return;
    setJoinRequestsLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/join-requests`), { headers: authHeaders() });
      const data = await res.json().catch(() => []);
      setJoinRequests(Array.isArray(data) ? data : []);
    } catch {
      // non-critical
    } finally {
      setJoinRequestsLoading(false);
    }
  }, [id]);

  const resolveJoinRequest = async (requestId: string, action: 'accept' | 'reject') => {
    if (processingJoinReqId) return;
    setProcessingJoinReqId(requestId);
    try {
      const res = await fetch(getApiUrl(`/api/groups/join-requests/${requestId}/${action}`), {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || 'No se pudo procesar la solicitud', 'error');
        return;
      }
      setJoinRequests((prev) => prev.filter((r) => r.id !== requestId));
      showNotification(action === 'accept' ? 'Solicitud aceptada' : 'Solicitud rechazada', 'success');
      if (action === 'accept') refetch();
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setProcessingJoinReqId(null);
    }
  };

  const addPendingMember = async () => {
    if (!id || !pendingForm.guestName.trim() || pendingFormSubmitting) return;
    setPendingFormSubmitting(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/pending-members`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(pendingForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || 'Error al agregar miembro', 'error');
        return;
      }
      showNotification('Jugador agregado al grupo', 'success');
      setPendingForm({ guestName: '', guestPosition: 'Mediocampista', guestRating: 3 });
      setShowAddPendingModal(false);
      fetchPendingMembers();
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setPendingFormSubmitting(false);
    }
  };

  const deletePendingMember = async (memberId: string) => {
    if (!id) return;
    try {
      await fetch(getApiUrl(`/api/groups/${id}/pending-members/${memberId}`), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      setPendingMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch {
      showNotification('Error al eliminar', 'error');
    }
  };

  const searchUsers = useCallback(async () => {
    if (!addMembersQuery.trim() || addMembersQuery.trim().length < 2) {
      showNotification('Escribí al menos 2 caracteres para buscar', 'warning');
      return;
    }
    setAddMembersSearching(true);
    try {
      const res = await fetch(
        `${getApiUrl('/api/users/search')}?q=${encodeURIComponent(addMembersQuery.trim())}&page=1&limit=30`,
        { headers: authHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      setAddMembersResults(data.users || []);
      setAddMembersSelected(new Set());
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setAddMembersSearching(false);
    }
  }, [addMembersQuery, showNotification]);

  const toggleAddMember = (userId: string) => {
    setAddMembersSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const fetchAnnouncements = useCallback(async () => {
    if (!id) return;
    setAnnouncementsLoading(true);
    setAnnouncementsError('');
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/announcements`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAnnouncementsError(data.error || 'No se pudieron cargar los anuncios');
        return;
      }
      setAnnouncements(data.announcements || []);
    } catch {
      setAnnouncementsError('Error de conexión al cargar anuncios');
    } finally {
      setAnnouncementsLoading(false);
    }
  }, [id]);

  const fetchGroupStats = useCallback(async () => {
    if (!id) return;
    setGroupStatsLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/stats`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudieron cargar estadísticas', 'error');
        return;
      }
      setGroupStats(data);
    } catch {
      showNotification('Error de conexión al cargar estadísticas', 'error');
    } finally {
      setGroupStatsLoading(false);
    }
  }, [id, showNotification]);

  const fetchMyGroupStats = useCallback(async () => {
    if (!id) return;
    setMyGroupStatsLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/analytics/me`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setMyGroupStats(data);
    } catch {
      // non-blocking
    } finally {
      setMyGroupStatsLoading(false);
    }
  }, [id]);

  const fetchGroupMatches = useCallback(async () => {
    if (!id) return;
    setMatchesLoading(true);
    try {
      const [upcomingRes, pastRes] = await Promise.all([
        fetch(getApiUrl(`/api/groups/${id}/matches?status=upcoming&limit=5`), { headers: authHeaders() }),
        fetch(getApiUrl(`/api/groups/${id}/matches?status=past&limit=5`), { headers: authHeaders() }),
      ]);
      const [upcomingData, pastData] = await Promise.all([
        upcomingRes.json().catch(() => ({})),
        pastRes.json().catch(() => ({})),
      ]);
      if (upcomingRes.ok) setUpcomingMatches(upcomingData.matches || []);
      if (pastRes.ok) setPastMatches(pastData.matches || []);
    } catch {
      showNotification('Error de conexión al cargar partidos del grupo', 'error');
    } finally {
      setMatchesLoading(false);
    }
  }, [id, showNotification]);

  const fetchRecurringMatch = useCallback(async () => {
    if (!id) return;
    setRecurringMatchLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/recurring-matches`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      setRecurringMatches(data.recurringMatches || []);
    } catch { /* silently ignore */ } finally {
      setRecurringMatchLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const togglePauseRecurringMatch = async (rm: RecurringMatchSeries) => {
    if (!id) return;
    setPausingRecurringId(rm.id);
    try {
      const endpoint = rm.isActive ? 'pause' : 'resume';
      const res = await fetch(getApiUrl(`/api/groups/${id}/recurring-matches/${rm.id}/${endpoint}`), {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      if (res.ok) await fetchRecurringMatch();
    } catch { /* silently ignore */ } finally {
      setPausingRecurringId(null);
    }
  };

  const confirmRecurringMatch = async (rm: RecurringMatchSeries) => {
    if (!id) return;
    setConfirmingRecurringId(rm.id);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/recurring-matches/${rm.id}/confirm`), {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      if (res.ok) {
        showNotification('Recurrente confirmado', 'success');
        await fetchRecurringMatch();
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || 'No se pudo confirmar', 'error');
      }
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setConfirmingRecurringId(null);
    }
  };

  const createNextRecurring = async (rm: RecurringMatchSeries) => {
    if (!id || creatingNextRecurringId) return;
    setCreatingNextRecurringId(rm.id);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/recurring-matches/${rm.id}/create-next`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showNotification(
          data?.date ? `Próximo partido creado para el ${data.date}` : 'Próximo partido creado',
          'success'
        );
        fetchGroupMatches();
        fetchRecurringMatch();
      } else {
        showNotification(data.error || 'No se pudo crear el próximo partido', 'error');
      }
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setCreatingNextRecurringId(null);
    }
  };

  const deleteRecurringMatch = async (rm: RecurringMatchSeries) => {
    if (!id) return;
    const ok = window.confirm(
      `¿Eliminar el partido recurrente "${rm.name || 'sin nombre'}"? Los partidos ya generados se mantienen, pero no se crearán nuevos.`
    );
    if (!ok) return;
    setDeletingRecurringId(rm.id);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/recurring-matches/${rm.id}`), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        showNotification('Recurrente eliminado', 'info');
        setRecurringMatches((prev) => prev.filter((r) => r.id !== rm.id));
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || 'No se pudo eliminar', 'error');
      }
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setDeletingRecurringId(null);
    }
  };

  const sendAnnouncement = async () => {
    if (!id || !announcementMessage.trim() || announcementSending) return;
    if (announcementMessage.trim().length > 500) {
      showNotification('El anuncio no puede superar 500 caracteres', 'warning');
      return;
    }
    setAnnouncementSending(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/announcements`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ message: announcementMessage.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo enviar el anuncio', 'error');
        return;
      }
      showNotification(`Anuncio enviado a ${data.recipientsCount ?? 0} miembro(s)`, 'success');
      setShowAnnouncementModal(false);
      setAnnouncementMessage('');
      fetchAnnouncements();
    } catch {
      showNotification('Error de conexión al enviar anuncio', 'error');
    } finally {
      setAnnouncementSending(false);
    }
  };

  const inviteToGroup = async () => {
    if (!id || addMembersSelected.size === 0 || addMembersSubmitting) return;
    setAddMembersSubmitting(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/invite`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ userIds: Array.from(addMembersSelected) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'Error al enviar invitaciones', 'error');
        return;
      }
      showNotification(
        data.invitedCount > 0
          ? `Invitación enviada a ${data.invitedCount} usuario(s). Deben aceptar para unirse.`
          : data.message || 'Invitaciones enviadas',
        'success'
      );
      setShowAddMembersModal(false);
      setAddMembersQuery('');
      setAddMembersResults([]);
      setAddMembersSelected(new Set());
      refetch();
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setAddMembersSubmitting(false);
    }
  };

  const [cancellingInvitationId, setCancellingInvitationId] = useState<string | null>(null);
  const cancelInvitation = async (invitationId: string, userName: string) => {
    if (!id || cancellingInvitationId) return;
    if (!window.confirm(`¿Cancelar la invitación a ${userName}?`)) return;
    setCancellingInvitationId(invitationId);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/invitations/${invitationId}`), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'Error al cancelar la invitación', 'error');
        return;
      }
      showNotification('Invitación cancelada', 'success');
      refetch();
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setCancellingInvitationId(null);
    }
  };

  const updateGroup = async () => {
    if (!id || !editGroupName.trim() || editGroupSubmitting) return;
    setEditGroupSubmitting(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}`), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ name: editGroupName.trim(), description: editGroupDescription.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo actualizar el grupo', 'error');
        return;
      }
      showNotification('Grupo actualizado', 'success');
      setShowEditGroupModal(false);
      refetch();
    } catch {
      showNotification('Error de conexión al actualizar grupo', 'error');
    } finally {
      setEditGroupSubmitting(false);
    }
  };

  const reactToAnnouncement = async (announcementId: string, emoji = '👍') => {
    if (!id) return;
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/announcements/${announcementId}/react`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ emoji })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      // Actualizar estado local
      setAnnouncements(prev => prev.map(a => {
        if (a.id !== announcementId) return a;
        const current = a.reactions?.[emoji] || 0;
        const wasReacted = a.userReacted;
        return {
          ...a,
          userReacted: !wasReacted,
          reactions: {
            ...a.reactions,
            [emoji]: wasReacted ? Math.max(0, current - 1) : current + 1
          }
        };
      }));
    } catch {
      // ignorar error de red
    }
  };

  const deleteAnnouncement = async (announcementId: string) => {
    if (!id) return;
    if (!window.confirm('¿Borrar este anuncio? No se puede deshacer.')) return;
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/announcements/${announcementId}`), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || 'No se pudo borrar el anuncio', 'error');
        return;
      }
      setAnnouncements((prev) => prev.filter((a) => a.id !== announcementId));
      showNotification('Anuncio borrado', 'success');
    } catch {
      showNotification('Error de conexión', 'error');
    }
  };

  const toggleGroupNotif = async () => {
    const membershipId = (group as any)?.myMembershipId;
    if (!membershipId || groupNotifSaving) return;
    const next = !groupNotifEnabled;
    setGroupNotifEnabled(next); // optimista
    setGroupNotifSaving(true);
    try {
      const res = await fetch(getApiUrl(`/api/notification-preferences/group/${membershipId}`), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ receiveMatchNotifications: next }),
      });
      if (!res.ok) {
        setGroupNotifEnabled(!next); // revertir
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || 'No se pudo guardar la preferencia', 'error');
      }
    } catch {
      setGroupNotifEnabled(!next);
      showNotification('Error de conexión', 'error');
    } finally {
      setGroupNotifSaving(false);
    }
  };

  const fetchPolls = useCallback(async () => {
    if (!id) return;
    setPollsLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/polls`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({ polls: [] }));
      setPolls(Array.isArray(data.polls) ? data.polls : []);
    } catch {
      // non-critical
    } finally {
      setPollsLoading(false);
    }
  }, [id]);

  // Mantener el handler de eventos de grupo siempre fresco (corre cada render).
  groupEventHandlerRef.current = (data: any) => {
    if (!data?.type) return;
    switch (data.type) {
      case 'MEMBER_REMOVED':
        if (data.userId === authUser?.id) {
          showNotification('Ya no formás parte de este grupo.', 'info');
          navigate('/groups');
        } else {
          refetch();
        }
        break;
      case 'GROUP_DELETED':
        showNotification('Este grupo fue eliminado.', 'info');
        navigate('/groups');
        break;
      case 'GROUP_UPDATED':
      case 'ROLE_CHANGED':
      case 'OWNERSHIP_TRANSFERRED':
        refetch();
        break;
      case 'POLL_CREATED':
        fetchPolls();
        break;
      case 'ANNOUNCEMENT_CREATED':
        fetchAnnouncements();
        break;
      default:
        break;
    }
  };

  const votePoll = async (pollId: string, optionId: string) => {
    if (votingPollId) return;
    setVotingPollId(pollId);
    try {
      const res = await fetch(getApiUrl(`/api/polls/${pollId}/vote`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ optionId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo votar', 'error');
        return;
      }
      setPolls((prev) => prev.map((p) => (p.id === pollId ? data.poll : p)));
      // Si sincroniza con un partido, refrescar la lista de partidos/widget.
      if (data.poll?.matchId) fetchGroupMatches();
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setVotingPollId(null);
    }
  };

  const closePoll = async (pollId: string) => {
    if (!window.confirm('¿Cerrar esta encuesta? No se podrá votar más.')) return;
    try {
      const res = await fetch(getApiUrl(`/api/polls/${pollId}/close`), { method: 'POST', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo cerrar', 'error');
        return;
      }
      setPolls((prev) => prev.map((p) => (p.id === pollId ? data.poll : p)));
    } catch {
      showNotification('Error de conexión', 'error');
    }
  };

  const deletePoll = async (pollId: string) => {
    if (!window.confirm('¿Eliminar esta encuesta? Se borra para todos y no se puede deshacer.')) return;
    try {
      const res = await fetch(getApiUrl(`/api/polls/${pollId}`), { method: 'DELETE', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo eliminar', 'error');
        return;
      }
      setPolls((prev) => prev.filter((p) => p.id !== pollId));
      showNotification('Encuesta eliminada', 'success');
    } catch {
      showNotification('Error de conexión', 'error');
    }
  };

  const quickJoinMatch = async (m: GroupMatchItem) => {
    if (quickJoiningId || !authUser) return;
    setQuickJoiningId(m.id);
    try {
      const isFull = m.playersJoined >= m.maxPlayers;
      const res = await fetch(getApiUrl(`/api/matches/${m.id}/join`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: authUser.name,
          position: (authUser as any).position || 'Mediocampista',
          rating: (authUser as any).rating || 3,
          isSubstitute: isFull,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo anotar', 'error');
        return;
      }
      showNotification(isFull ? 'Te anotaste como suplente' : '¡Te anotaste!', 'success');
      fetchGroupMatches();
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setQuickJoiningId(null);
    }
  };

  const sendQuickReminder = async (matchInfo: GroupMatchItem) => {
    if (!id) return;
    const message = `⏰ Recordatorio: el partido del ${matchInfo.date} a las ${matchInfo.time} en ${matchInfo.location} ya está cerca. Confirmá tu asistencia si todavía no lo hiciste.`;
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/announcements`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ message })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo enviar el recordatorio', 'error');
        return;
      }
      showNotification(`Recordatorio enviado a ${data.recipientsCount ?? 0} miembro(s)`, 'success');
    } catch {
      showNotification('Error de conexión', 'error');
    }
  };

  const generateInviteLink = async () => {
    if (!id || linkGenerating) return;
    setLinkGenerating(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/invite-link`), {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo generar el link', 'error');
        return;
      }
      // Código de referido: quien entre por el link queda contado al registrarse.
      let refCode: string | null = null;
      try {
        const refRes = await fetch(getApiUrl('/api/referrals/code'), { headers: authHeaders() });
        if (refRes.ok) {
          const refData = await refRes.json().catch(() => ({}));
          refCode = refData?.referralCode || null;
        }
      } catch {
        /* sin código: el link igual funciona */
      }
      const appUrl = process.env.REACT_APP_CLIENT_URL || window.location.origin;
      const link = `${appUrl}/join-group/${data.token}${refCode ? `?ref=${refCode}` : ''}`;
      setInviteLink(link);
      setShowShareModal(true);
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setLinkGenerating(false);
    }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      showNotification('No se pudo copiar el link', 'error');
    }
  };

  const shareToWhatsApp = () => {
    const text = `Te invito al grupo de fútbol "${group?.name}" en Futbolero 🏟️\nHacé clic para unirte: ${inviteLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const changeMemberRole = async (memberId: string, nextRole: 'ADMIN' | 'MEMBER') => {
    if (!id || memberActionLoadingId) return;
    setMemberActionLoadingId(memberId);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/members/${memberId}/role`), {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ role: nextRole }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo cambiar el rol', 'error');
        return;
      }
      showNotification(`Rol actualizado a ${nextRole}`, 'success');
      refetch();
    } catch {
      showNotification('Error de conexión al cambiar rol', 'error');
    } finally {
      setMemberActionLoadingId(null);
    }
  };

  const removeMember = async (memberId: string, memberName: string) => {
    if (!id || memberActionLoadingId) return;
    if (!window.confirm(`¿Remover a ${memberName} del grupo?`)) return;
    setMemberActionLoadingId(memberId);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/members/${memberId}`), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo remover el miembro', 'error');
        return;
      }
      showNotification('Miembro removido', 'success');
      refetch();
    } catch {
      showNotification('Error de conexión al remover miembro', 'error');
    } finally {
      setMemberActionLoadingId(null);
    }
  };

  const transferLeadership = async (newCreatorId: string, memberName: string) => {
    if (!id || transferring) return;
    if (!window.confirm(`¿Transferir el liderazgo del grupo a ${memberName}? Vas a seguir siendo administrador, pero ${memberName} pasará a ser el creador/dueño del grupo.`)) return;
    setTransferring(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/transfer`), {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ newCreatorId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo transferir el liderazgo', 'error');
        return;
      }
      showNotification(`Liderazgo transferido a ${memberName}`, 'success');
      refetch();
    } catch {
      showNotification('Error de conexión al transferir liderazgo', 'error');
    } finally {
      setTransferring(false);
    }
  };

  const leaveOrDeleteGroup = async () => {
    if (!id || !authUser?.id) return;
    const isCreatorAdmin = group?.userRole === 'ADMIN' && group?.creatorId === authUser.id;
    const confirmation = isCreatorAdmin
      ? 'Sos el creador del grupo. ¿Querés eliminarlo?'
      : '¿Querés salir del grupo?';
    if (!window.confirm(confirmation)) return;
    try {
      const endpoint = isCreatorAdmin ? `/api/groups/${id}` : `/api/groups/${id}/members/${authUser.id}`;
      const res = await fetch(getApiUrl(endpoint), { method: 'DELETE', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'No se pudo completar la acción', 'error');
        return;
      }
      showNotification(isCreatorAdmin ? 'Grupo eliminado' : 'Saliste del grupo', 'success');
      navigate('/groups');
    } catch {
      showNotification('Error de conexión', 'error');
    }
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    if (!group) return;
    setEditGroupName(group.name || '');
    setEditGroupDescription(group.description || '');
    fetchAnnouncements();
  }, [group, fetchAnnouncements]);

  useEffect(() => {
    if (!group) return;
    if (activeTab === 'activity') {
      fetchGroupMatches();
      if (!recurringFetchedRef.current) { recurringFetchedRef.current = true; fetchRecurringMatch(); }
    }
    if (activeTab === 'info') fetchGroupStats();
    if (activeTab === 'ranking') fetchMyGroupStats();
    if (activeTab === 'chat') fetchPolls(); // encuestas embebidas en el chat
    if (activeTab === 'members' && group.userRole === 'ADMIN') {
      fetchPendingMembers();
      fetchJoinRequests();
    }
  }, [group, activeTab, fetchGroupMatches, fetchGroupStats, fetchRecurringMatch, fetchPendingMembers, fetchJoinRequests, fetchMyGroupStats, fetchPolls]);

  // Sincronizar el toggle de notificaciones del grupo con lo que viene del backend.
  useEffect(() => {
    if (group) setGroupNotifEnabled((group as any).receiveGroupMatchNotifications !== false);
  }, [group]);

  // Deep-link desde notificación de anuncio: abrir tab actividad y resaltar el anuncio.
  useEffect(() => {
    if (highlightAnnouncementId) setActiveTab('activity');
    // 'polls' ya no es un tab: las encuestas viven dentro del chat.
    else if (initialTabParam === 'polls') setActiveTab('chat');
    else if (initialTabParam && ['members', 'activity', 'chat', 'ranking', 'seasons', 'info'].includes(initialTabParam)) {
      setActiveTab(initialTabParam as GroupTab);
    }
  }, [highlightAnnouncementId, initialTabParam]);

  useEffect(() => {
    if (!highlightAnnouncementId || announcements.length === 0) return;
    const el = document.getElementById(`announcement-${highlightAnnouncementId}`);
    if (el) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
      return () => clearTimeout(t);
    }
  }, [highlightAnnouncementId, announcements]);

  // Cargar próximos partidos al iniciar para el widget "Próximo partido" (todos los miembros)
  useEffect(() => {
    if (!group) return;
    if (upcomingMatches.length === 0 && !matchesLoading) {
      fetchGroupMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  if (!id) {
    navigate('/groups');
    return null;
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: theme.colors.background, padding: isMobile ? '80px 16px 20px' : '20px 40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 40, height: 40, border: `3px solid ${theme.colors.border}`, borderTopColor: theme.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (!group) {
    return (
      <div style={{ minHeight: '100vh', background: theme.colors.background, padding: isMobile ? '80px 16px 20px' : '20px 40px' }}>
        <AppHeader title="Grupo" showBackButton />
        <p style={{ color: theme.colors.textSecondary, textAlign: 'center', marginTop: 24 }}>No se pudo cargar el grupo.</p>
      </div>
    );
  }

  return (
    <div className="group-page-calm" style={{ minHeight: '100vh', background: theme.colors.background, padding: isMobile ? '80px 16px 20px' : '20px 40px' }}>
      <AppHeader title={group.name} showBackButton />

      <div style={{ background: theme.colors.surface, borderRadius: 20, padding: 24, marginBottom: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: `1px solid ${theme.colors.border}` }}>
        {group.description && (
          <p style={{ color: theme.colors.textSecondary, fontSize: 14, margin: '0 0 16px 0', whiteSpace: 'pre-wrap' }}>
            {group.description}
          </p>
        )}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 14, color: theme.colors.textSecondary }}>👥 {group.memberCount} miembros</span>
          <span style={{ fontSize: 14, color: theme.colors.textSecondary }}>⚽ {group.matchCount} partidos</span>
          <span style={{ fontSize: 14, color: group.userRole === 'ADMIN' ? theme.colors.primary : theme.colors.textSecondary, fontWeight: 600 }}>
            {group.userRole === 'ADMIN' ? 'Admin' : 'Miembro'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
          <button
            type="button"
            onClick={leaveOrDeleteGroup}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              color: '#FF6B6B',
              border: `1px solid #FF6B6B`,
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: theme.fonts.body,
              letterSpacing: 'normal',
              boxShadow: 'none',
              cursor: 'pointer',
            }}
          >
            {group.userRole === 'ADMIN' && group.creatorId === authUser?.id ? 'Eliminar grupo' : 'Salir del grupo'}
          </button>
          {group.userRole === 'ADMIN' && (
            <>
              <button
                type="button"
                onClick={generateInviteLink}
                disabled={linkGenerating}
                style={{
                  padding: '8px 14px',
                  background: theme.colors.primary,
                  color: theme.colors.background,
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: theme.fonts.body,
                  letterSpacing: 'normal',
                  boxShadow: 'none',
                  cursor: linkGenerating ? 'not-allowed' : 'pointer',
                  opacity: linkGenerating ? 0.7 : 1,
                }}
              >
                {linkGenerating ? '...' : '🔗 Compartir grupo'}
              </button>
              <button
                type="button"
                onClick={() => setShowEditGroupModal(true)}
                style={{
                  padding: '8px 14px',
                  background: '#1E3A2E',
                  color: '#4CAF50',
                  border: `1px solid #2E7D32`,
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: theme.fonts.body,
                  letterSpacing: 'normal',
                  boxShadow: 'none',
                  cursor: 'pointer',
                }}
              >
                Editar grupo
              </button>
            </>
          )}
        </div>

        {/* Dashboard del organizador: próximo partido */}
        {upcomingMatches.length > 0 && (() => {
          const nextMatch = upcomingMatches[0];
          const confirmed = nextMatch.playersJoined;
          const total = nextMatch.maxPlayers;
          const pending = total - confirmed;
          const isFull = confirmed >= total;
          return (
            <div style={{
              background: 'rgba(0,230,118,0.07)',
              border: `1px solid ${alpha('primary', '30')}`,
              borderRadius: 14,
              padding: '14px 16px',
              marginBottom: 18
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.primary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                ⚽ Próximo partido
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: theme.colors.text, marginBottom: 2 }}>
                {nextMatch.location}
              </div>
              <div style={{ fontSize: 13, color: theme.colors.textSecondary, marginBottom: 10 }}>
                {nextMatch.date} · {nextMatch.time}
              </div>

              {/* Confirmados */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ flex: 1, background: `${theme.colors.border}`, borderRadius: 6, height: 8, overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, (confirmed / total) * 100)}%`,
                    height: '100%',
                    background: isFull ? '#FF6B6B' : theme.colors.primary,
                    borderRadius: 6,
                    transition: 'width 0.3s'
                  }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: isFull ? '#FF6B6B' : theme.colors.primary, whiteSpace: 'nowrap' }}>
                  {confirmed}/{total}
                </span>
              </div>

              {/* Alerta */}
              {!isFull && pending > 0 && (
                <div style={{ fontSize: 12, color: '#F59E0B', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ⚠️ Faltan {pending} confirmación{pending !== 1 ? 'es' : ''}
                </div>
              )}
              {isFull && (
                <div style={{ fontSize: 12, color: theme.colors.primary, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ✅ Partido completo
                </div>
              )}

              {/* Acciones rápidas */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => navigate(`/match/${nextMatch.id}`)}
                  style={{
                    flex: 1,
                    padding: '9px 12px',
                    background: theme.colors.primary,
                    color: theme.colors.background,
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    minWidth: 110
                  }}
                >
                  Ver partido
                </button>
                {nextMatch.userPlayer ? (
                  <span style={{
                    flex: 1, minWidth: 110, padding: '9px 12px', textAlign: 'center',
                    color: theme.colors.primary, fontSize: 13, fontWeight: 700,
                    border: `1px solid ${theme.colors.primary}`, borderRadius: 10,
                  }}>
                    ✓ Ya estás anotado
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => quickJoinMatch(nextMatch)}
                    disabled={quickJoiningId === nextMatch.id}
                    style={{
                      flex: 1, padding: '9px 12px',
                      background: theme.colors.primary, color: theme.colors.background,
                      border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
                      cursor: quickJoiningId === nextMatch.id ? 'not-allowed' : 'pointer', minWidth: 110,
                    }}
                  >
                    {quickJoiningId === nextMatch.id ? '...' : (nextMatch.playersJoined >= nextMatch.maxPlayers ? '⚡ Anotarme (suplente)' : '⚡ Me anoto')}
                  </button>
                )}
                {group.userRole === 'ADMIN' && (
                  <button
                    type="button"
                    onClick={() => sendQuickReminder(nextMatch)}
                    style={{
                      flex: 1,
                      padding: '9px 12px',
                      background: 'transparent',
                      color: theme.colors.primary,
                      border: `1px solid ${theme.colors.primary}`,
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      minWidth: 110
                    }}
                  >
                    📢 Recordar a todos
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        <div
          className="hide-scrollbar"
          style={{ display: 'flex', gap: 8, marginBottom: 18, borderBottom: `1px solid ${theme.colors.border}`, paddingBottom: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
        >
          {[
            { key: 'members', label: 'Miembros' },
            { key: 'chat', label: '💬 Chat' },
            { key: 'activity', label: 'Actividad' },
            { key: 'ranking', label: 'Ranking' },
            { key: 'seasons', label: 'Temporadas' },
            { key: 'info', label: 'Info' },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key as GroupTab)}
              style={{
                position: 'relative',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                padding: '8px 12px',
                borderRadius: 10,
                border: `1px solid ${activeTab === tab.key ? theme.colors.primary : theme.colors.border}`,
                background: activeTab === tab.key ? 'rgba(0,230,118,0.12)' : 'transparent',
                color: activeTab === tab.key ? theme.colors.primary : theme.colors.textSecondary,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {tab.label}
              {tab.key === 'chat' && chatUnread > 0 && (
                <span
                  style={{
                    background: theme.colors.danger,
                    color: '#fff',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    minWidth: 18,
                    height: 18,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 5px',
                  }}
                >
                  {chatUnread > 9 ? '9+' : chatUnread}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'members' && (
          <>
            {group.userRole === 'ADMIN' && (
              <div style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => setShowAddMembersModal(true)}
                  style={{
                    padding: '8px 16px',
                    background: theme.colors.primary,
                    color: theme.colors.background,
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  + Invitar al grupo
                </button>
              </div>
            )}

            {/* Solicitudes de ingreso (por link) — solo admin */}
            {group.userRole === 'ADMIN' && !joinRequestsLoading && joinRequests.length > 0 && (
              <div style={{
                marginBottom: 16,
                border: `1px solid ${theme.colors.primary}55`,
                background: `${theme.colors.primary}12`,
                borderRadius: 12,
                padding: 12,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
                  color: theme.colors.text, fontWeight: 700, fontSize: 14,
                }}>
                  <span>📥</span>
                  Solicitudes de ingreso
                  <span style={{
                    background: theme.colors.primary, color: theme.colors.background,
                    borderRadius: 10, minWidth: 20, height: 20, padding: '0 6px',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                  }}>
                    {joinRequests.length}
                  </span>
                </div>
                {joinRequests.map((jr) => (
                  <div key={jr.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0', borderTop: `1px solid ${theme.colors.border || '#33383B'}`,
                  }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 17, overflow: 'hidden', flexShrink: 0,
                      background: theme.colors.primary, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: theme.colors.background, fontWeight: 700, fontSize: 14,
                    }}>
                      {jr.user.avatar
                        ? <img src={jr.user.avatar} alt={jr.user.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : (jr.user.name?.[0]?.toUpperCase() || '?')}
                    </div>
                    <span style={{ flex: 1, color: theme.colors.text, fontSize: 14, fontWeight: 600 }}>
                      {jr.user.name}
                    </span>
                    <button
                      type="button"
                      disabled={processingJoinReqId === jr.id}
                      onClick={() => resolveJoinRequest(jr.id, 'accept')}
                      style={{
                        padding: '6px 12px', background: '#00C853', color: '#fff', border: 'none',
                        borderRadius: 8, fontSize: 13, fontWeight: 700,
                        cursor: processingJoinReqId === jr.id ? 'not-allowed' : 'pointer',
                        opacity: processingJoinReqId === jr.id ? 0.6 : 1,
                      }}
                    >
                      Aceptar
                    </button>
                    <button
                      type="button"
                      disabled={processingJoinReqId === jr.id}
                      onClick={() => resolveJoinRequest(jr.id, 'reject')}
                      style={{
                        padding: '6px 12px', background: 'transparent', color: '#F44336',
                        border: '1px solid #F4433655', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        cursor: processingJoinReqId === jr.id ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Rechazar
                    </button>
                  </div>
                ))}
              </div>
            )}

            {group.members.length >= 2 && (
              <div style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  data-testid="h2h-open-picker"
                  onClick={() => setH2hPickerOpen(true)}
                  style={{
                    background: '#2a4d8f',
                    color: '#fff',
                    padding: '10px 18px',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  ⚔️ Enfrentar jugadores
                </button>
              </div>
            )}

            <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: '0 0 12px 0' }}>Miembros</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {group.members.map((m) => {
                const isMe = m.id === authUser?.id;
                const canManage = group.userRole === 'ADMIN' && !isMe && m.id !== group.creatorId;
                const canTransfer = authUser?.id === group.creatorId && !isMe && m.role === 'ADMIN';
                return (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => navigate(`/public-profile/${m.id}`)}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/public-profile/${m.id}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      rowGap: 8,
                      gap: 12,
                      padding: '10px 14px',
                      background: theme.colors.background,
                      borderRadius: 12,
                      border: `1px solid ${theme.colors.border}`,
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      fontFamily: theme.fonts.body,
                      letterSpacing: 'normal',
                      boxShadow: 'none',
                    }}
                  >
                    <Avatar src={m.avatar} name={m.name} size={40} bg={theme.colors.primary} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: theme.colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
                      {m.rating != null && <div style={{ fontSize: 12, color: theme.colors.textSecondary }}>⭐ {m.rating.toFixed(1)}</div>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, color: m.role === 'ADMIN' ? theme.colors.primary : theme.colors.textSecondary, fontWeight: m.role === 'ADMIN' ? 600 : 400 }}>
                        {m.role === 'ADMIN' ? 'Admin' : 'Miembro'}
                      </span>
                      {m.id === group.creatorId && (
                        <span style={{ fontSize: 10, color: theme.colors.textSecondary }}>Creador</span>
                      )}
                    </div>
                    {canManage && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', ...(isMobile ? { flexBasis: '100%' } : {}) }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
                        <button
                          type="button"
                          disabled={memberActionLoadingId === m.id}
                          onClick={() => changeMemberRole(m.id, m.role === 'ADMIN' ? 'MEMBER' : 'ADMIN')}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: `1px solid ${theme.colors.border}`,
                            background: 'transparent',
                            color: theme.colors.textSecondary,
                            fontSize: 12,
                            fontFamily: theme.fonts.body,
                            fontWeight: 500,
                            letterSpacing: 'normal',
                            boxShadow: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          {m.role === 'ADMIN' ? 'Quitar admin' : 'Hacer admin'}
                        </button>
                        <button
                          type="button"
                          disabled={memberActionLoadingId === m.id}
                          onClick={() => removeMember(m.id, m.name)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: '1px solid #FF6B6B',
                            background: 'transparent',
                            color: '#FF6B6B',
                            fontSize: 12,
                            fontFamily: theme.fonts.body,
                            fontWeight: 500,
                            letterSpacing: 'normal',
                            boxShadow: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          Remover
                        </button>
                      </div>
                    )}
                    {canTransfer && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', ...(isMobile ? { flexBasis: '100%' } : {}) }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
                        <button
                          type="button"
                          disabled={transferring}
                          onClick={() => transferLeadership(m.id, m.name)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: `1px solid #A78BFA`,
                            background: 'transparent',
                            color: '#A78BFA',
                            fontSize: 12,
                            fontFamily: theme.fonts.body,
                            fontWeight: 500,
                            letterSpacing: 'normal',
                            boxShadow: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          👑 Transferir
                        </button>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {group.userRole === 'ADMIN' && (group.pendingInvitations?.length ?? 0) > 0 && (
              <>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: '20px 0 12px 0' }}>
                  Invitaciones pendientes
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {group.pendingInvitations!.map((inv) => (
                    <div
                      key={inv.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 14px',
                        background: theme.colors.background,
                        borderRadius: 12,
                        border: `1px dashed ${theme.colors.border}`,
                      }}
                    >
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: inv.userAvatar ? 'transparent' : theme.colors.secondary, overflow: 'hidden', flexShrink: 0 }}>
                        {inv.userAvatar ? (
                          <img src={inv.userAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 18 }}>👤</span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: theme.colors.text }}>{inv.userName}</div>
                        <div style={{ fontSize: 12, color: theme.colors.textSecondary }}>Esperando que acepte</div>
                      </div>
                      <button
                        onClick={() => cancelInvitation(inv.id, inv.userName)}
                        disabled={cancellingInvitationId === inv.id}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${theme.colors.danger}`,
                          color: theme.colors.danger,
                          borderRadius: 8,
                          padding: '6px 12px',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: cancellingInvitationId === inv.id ? 'default' : 'pointer',
                          opacity: cancellingInvitationId === inv.id ? 0.6 : 1,
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {cancellingInvitationId === inv.id ? 'Cancelando…' : 'Cancelar'}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Jugadores del grupo sin cuenta (pending members) */}
            {group.userRole === 'ADMIN' && (
              <div style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: 0 }}>
                    Jugadores sin cuenta
                    {pendingMembers.length > 0 && (
                      <span style={{ marginLeft: 8, fontSize: 12, background: 'rgba(255,165,0,0.15)', color: '#FFA500', padding: '2px 8px', borderRadius: 8, fontWeight: 500 }}>
                        {pendingMembers.length}
                      </span>
                    )}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowAddPendingModal(true)}
                    style={{ padding: '6px 12px', background: 'transparent', border: `1px solid ${theme.colors.primary}`, color: theme.colors.primary, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    + Agregar
                  </button>
                </div>

                {pendingMembersLoading ? (
                  <div style={{ color: theme.colors.textSecondary, fontSize: 13 }}>Cargando...</div>
                ) : pendingMembers.length === 0 ? (
                  <div style={{ padding: '12px 14px', background: theme.colors.background, borderRadius: 12, border: `1px dashed ${theme.colors.border}`, color: theme.colors.textSecondary, fontSize: 13 }}>
                    Agregá a tus jugadores habituales aunque no tengan cuenta en Futbolero. Sus stats se acumularán en cada partido.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {pendingMembers.map((pm) => (
                      <div
                        key={pm.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: theme.colors.background, borderRadius: 12, border: `1px dashed rgba(255,165,0,0.3)` }}
                      >
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,165,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: 18 }}>👻</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: theme.colors.text }}>{pm.guestName}</div>
                          <div style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                            {pm.totalMatches > 0
                              ? `${pm.totalMatches} partido${pm.totalMatches !== 1 ? 's' : ''} · ${pm.totalGoals} ⚽ · ${pm.totalAssists} 🅰️`
                              : `${pm.guestPosition} · Sin partidos aún`}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, background: 'rgba(255,165,0,0.15)', color: '#FFA500', padding: '2px 8px', borderRadius: 8, fontWeight: 500 }}>
                          Sin cuenta
                        </span>
                        <button
                          type="button"
                          onClick={() => deletePendingMember(pm.id)}
                          style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: 16, padding: '4px' }}
                          title="Eliminar"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Modal: agregar jugador sin cuenta */}
            {showAddPendingModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
                <div style={{ background: theme.colors.surface, borderRadius: 16, padding: 24, width: '100%', maxWidth: 360, border: `1px solid ${theme.colors.border}`, overflow: 'hidden' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, margin: '0 0 16px 0' }}>Agregar jugador sin cuenta</h3>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, color: theme.colors.textSecondary, fontWeight: 600, display: 'block', marginBottom: 6 }}>Nombre</label>
                    <input
                      type="text"
                      value={pendingForm.guestName}
                      onChange={(e) => setPendingForm((f) => ({ ...f, guestName: e.target.value }))}
                      placeholder="Ej: Tomy F."
                      style={{ width: '100%', padding: '10px 12px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: 10, color: theme.colors.text, fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, color: theme.colors.textSecondary, fontWeight: 600, display: 'block', marginBottom: 6 }}>Posición</label>
                    <select
                      value={pendingForm.guestPosition}
                      onChange={(e) => setPendingForm((f) => ({ ...f, guestPosition: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: 10, color: theme.colors.text, fontSize: 14, boxSizing: 'border-box' }}
                    >
                      {['Arquero', 'Defensor', 'Mediocampista', 'Delantero'].map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 12, color: theme.colors.textSecondary, fontWeight: 600, display: 'block', marginBottom: 6 }}>Nivel (1-5)</label>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setPendingForm((f) => ({ ...f, guestRating: star }))}
                          style={{ fontSize: 22, background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 2px', margin: 0, lineHeight: 1, color: star <= pendingForm.guestRating ? '#FFD700' : '#555' }}
                        >
                          {star <= pendingForm.guestRating ? '★' : '☆'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => setShowAddPendingModal(false)}
                      style={{ flex: 1, padding: '10px 0', background: 'transparent', border: `1px solid ${theme.colors.border}`, color: theme.colors.textSecondary, borderRadius: 10, fontSize: 14, cursor: 'pointer' }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={addPendingMember}
                      disabled={!pendingForm.guestName.trim() || pendingFormSubmitting}
                      style={{ flex: 1, padding: '10px 0', background: theme.colors.primary, border: 'none', color: theme.colors.background, borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: !pendingForm.guestName.trim() ? 0.5 : 1 }}
                    >
                      {pendingFormSubmitting ? 'Agregando...' : 'Agregar'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'activity' && (
          <>
            {/* Admin: botones Crear partido + Crear recurrente + Anuncio */}
            {group.userRole === 'ADMIN' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                <button
                  type="button"
                  onClick={() => navigate(`/create-match?groupId=${id}&groupName=${encodeURIComponent(group?.name ?? '')}`)}
                  style={{ flex: '1 1 140px', padding: '10px 14px', background: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                >
                  ＋ Crear partido
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateRecurringModal(true)}
                  style={{ padding: '10px 14px', background: theme.colors.surface, color: theme.colors.primary, border: `1px solid ${alpha('primary', '50')}`, borderRadius: 12, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  🔄 Crear recurrente
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddMatchesModal(true)}
                  style={{ padding: '10px 14px', background: theme.colors.surface, color: theme.colors.primary, border: `1px solid ${alpha('primary', '50')}`, borderRadius: 12, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  📥 Agregar jugados
                </button>
              </div>
            )}

            {/* Recurring match series cards */}
            {recurringMatchLoading ? (
              <div style={{ textAlign: 'center', padding: '8px 0', marginBottom: 12, color: theme.colors.textSecondary, fontSize: 13 }}>Cargando series...</div>
            ) : recurringMatches.length > 0 ? (
              recurringMatches.map((rm) => {
                const needsConfirm = !!rm.isActive && (rm.matchesSinceConfirmation ?? 0) >= 4;
                const hasLowTurnout = !!rm.isActive && (rm.consecutiveLowTurnout ?? 0) > 0;
                const status = !rm.isActive
                  ? { label: 'Pausado ⏸', color: '#EF4444', bg: 'rgba(239,68,68,0.15)' }
                  : needsConfirm
                  ? { label: 'Necesita confirmar', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' }
                  : hasLowTurnout
                  ? { label: 'Baja participación', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' }
                  : { label: 'Activo', color: theme.colors.primary, bg: theme.colors.primary + '20' };
                const freqLabel = rm.frequency === 'BIWEEKLY' ? 'Quincenal' : 'Semanal';
                return (
                <div key={rm.id} style={{ background: theme.colors.surface, border: `1.5px solid ${rm.isActive ? theme.colors.primary + '50' : theme.colors.border}`, borderRadius: 14, padding: '12px 16px', marginBottom: 12, opacity: rm.isActive ? 1 : 0.75 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: theme.colors.text }}>🔄 {rm.name || 'Partido fijo semanal'}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: status.bg, color: status.color }}>
                      {status.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: theme.colors.textSecondary, marginBottom: 10 }}>
                    {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'][rm.dayOfWeek]} · {rm.time}hs · {freqLabel} · {rm.templateMatch.location} · ${rm.templateMatch.price}
                  </div>

                  {hasLowTurnout && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8, marginBottom: 8, fontSize: 12, color: '#F59E0B' }}>
                      ⚠️ {rm.consecutiveLowTurnout} partido(s) seguidos con baja participación
                    </div>
                  )}
                  {needsConfirm && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(0,102,204,0.12)', border: '1px solid rgba(0,102,204,0.35)', borderRadius: 8, marginBottom: 8, fontSize: 12, color: '#0066CC' }}>
                      ℹ️ Se generaron {rm.matchesSinceConfirmation} partidos. ¿Seguir generando?
                    </div>
                  )}
                  {!rm.isActive && rm.pausedReason && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 8, marginBottom: 8, fontSize: 12, color: '#EF4444' }}>
                      ⏸ {rm.pausedReason}
                    </div>
                  )}

                  {rm.upcomingMatches && rm.upcomingMatches.length > 0 && (
                    <div style={{ marginTop: 8, marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        Próximos partidos ({rm.upcomingMatchesCount ?? rm.upcomingMatches.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {rm.upcomingMatches.slice(0, 3).map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => navigate(`/match/${m.id}`)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}
                          >
                            <span style={{ fontSize: 12, color: theme.colors.text }}>
                              {new Date(m.date).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })} · {m.time}hs
                            </span>
                            <span style={{ fontSize: 12, color: theme.colors.primary, fontWeight: 600 }}>
                              👥 {m.playerCount}/{m.maxPlayers}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {group.userRole === 'ADMIN' && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {needsConfirm && (
                        <button
                          type="button"
                          onClick={() => confirmRecurringMatch(rm)}
                          disabled={confirmingRecurringId === rm.id}
                          style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 8, border: 'none', background: '#F59E0B', color: '#fff', cursor: confirmingRecurringId === rm.id ? 'not-allowed' : 'pointer' }}
                        >
                          {confirmingRecurringId === rm.id ? '...' : '✓ Confirmar'}
                        </button>
                      )}
                      {rm.isActive && (
                        <button
                          type="button"
                          onClick={() => createNextRecurring(rm)}
                          disabled={creatingNextRecurringId === rm.id}
                          style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 8, border: 'none', background: theme.colors.primary, color: theme.colors.background, cursor: creatingNextRecurringId === rm.id ? 'not-allowed' : 'pointer' }}
                        >
                          {creatingNextRecurringId === rm.id ? '...' : '➕ Crear próximo'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => togglePauseRecurringMatch(rm)}
                        disabled={pausingRecurringId === rm.id}
                        style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 8, border: `1px solid ${theme.colors.primary}`, background: 'transparent', color: theme.colors.primary, cursor: pausingRecurringId === rm.id ? 'not-allowed' : 'pointer' }}
                      >
                        {pausingRecurringId === rm.id ? '...' : rm.isActive ? '⏸ Pausar' : '▶ Reactivar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteRecurringMatch(rm)}
                        disabled={deletingRecurringId === rm.id}
                        style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 8, border: `1px solid #EF4444`, background: 'transparent', color: '#EF4444', cursor: deletingRecurringId === rm.id ? 'not-allowed' : 'pointer' }}
                      >
                        {deletingRecurringId === rm.id ? '...' : '🗑 Eliminar'}
                      </button>
                    </div>
                  )}
                </div>
                );
              })
            ) : null}

            <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: '20px 0 10px 0' }}>Partidos del grupo</h3>
            {matchesLoading ? (
              <p style={{ color: theme.colors.textSecondary, margin: 0 }}>Cargando partidos...</p>
            ) : (
              <>
                <h4 style={{ fontSize: 14, color: theme.colors.primary, margin: '0 0 8px 0' }}>Próximos</h4>
                {upcomingMatches.length === 0 ? (
                  <p style={{ color: theme.colors.textSecondary, margin: '0 0 12px 0' }}>No hay partidos próximos.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                    {upcomingMatches.map((m) => {
                      const filled = m.playersJoined;
                      const total = m.maxPlayers;
                      const pct = total > 0 ? Math.min(100, Math.round((filled / total) * 100)) : 0;
                      return (
                        <div key={m.id} style={{ borderRadius: 14, background: theme.colors.surface, border: `1px solid ${theme.colors.border}`, overflow: 'hidden' }}>
                          {/* Date strip */}
                          <div style={{ padding: '10px 14px 6px', borderBottom: `1px solid ${alpha('border', '33')}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ color: theme.colors.text, fontWeight: 700, fontSize: 14 }}>{m.location}</div>
                              {m.recurringInstance && (
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: theme.colors.primary + '18', color: theme.colors.primary }}>🔄 Fijo</span>
                              )}
                            </div>
                            <div style={{ color: theme.colors.textSecondary, fontSize: 12, marginTop: 2 }}>{m.date} · {m.time}</div>
                          </div>
                          {/* Attendance bar */}
                          <div style={{ padding: '8px 14px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ fontSize: 12, color: theme.colors.textSecondary }}>Confirmados</span>
                              <span style={{ fontSize: 13, fontWeight: 700, color: pct >= 70 ? theme.colors.primary : theme.colors.textSecondary }}>
                                {filled}/{total}
                              </span>
                            </div>
                            <div style={{ height: 4, borderRadius: 2, background: theme.colors.border, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2, background: pct >= 70 ? theme.colors.primary : theme.colors.warning, transition: 'width 0.3s' }} />
                            </div>
                          </div>
                          {/* Actions */}
                          <div style={{ display: 'flex', gap: 8, padding: '0 14px 12px' }}>
                            <button
                              type="button"
                              onClick={() => navigate(`/match/${m.id}`)}
                              style={{ flex: 1, padding: '8px 0', background: 'transparent', color: theme.colors.primary, border: `1px solid ${theme.colors.primary}`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                            >
                              Ver partido
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const text = `⚽ Partido de fútbol\n📍 ${m.location}\n📅 ${m.date} a las ${m.time}\n👥 ${filled}/${total} confirmados\n\nVer en Futbolero: ${process.env.REACT_APP_CLIENT_URL || window.location.origin}/match/${m.id}`;
                                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                              }}
                              style={{ padding: '8px 12px', background: '#25D36618', color: '#25D366', border: `1px solid #25D36650`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                            >
                              💬
                            </button>
                            {group.userRole === 'ADMIN' && (
                              <button
                                type="button"
                                onClick={() => navigate(`/match/${m.id}#payments`)}
                                style={{ padding: '8px 12px', background: '#ffd60018', color: theme.colors.warning, border: `1px solid #ffd60040`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                                title="Gestionar cobros"
                              >
                                💰
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <h4 style={{ fontSize: 14, color: theme.colors.warning, margin: '0 0 8px 0' }}>Recientes</h4>
                {pastMatches.length === 0 ? (
                  <p style={{ color: theme.colors.textSecondary, margin: 0 }}>No hay historial de partidos.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {pastMatches.map((m) => {
                      const hasScore = typeof m.team1Goals === 'number' && typeof m.team2Goals === 'number' && m.team1Name && m.team2Name;
                      return (
                        <button
                          type="button"
                          key={m.id}
                          onClick={() => navigate(`/match/${m.id}`)}
                          style={{ padding: '10px 14px', borderRadius: 12, background: theme.colors.background, border: `1px solid ${theme.colors.border}`, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ color: theme.colors.text, fontWeight: 600, fontSize: 13 }}>{m.location}</div>
                              <div style={{ color: theme.colors.textSecondary, fontSize: 12, marginTop: 2 }}>{m.date} · {m.time}</div>
                            </div>
                            {hasScore && (
                              <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                                <div style={{ fontWeight: 700, fontSize: 15, color: theme.colors.text }}>{m.team1Goals} - {m.team2Goals}</div>
                                <div style={{ fontSize: 10, color: theme.colors.textSecondary }}>{m.team1Name} vs {m.team2Name}</div>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'ranking' && id && (
          <>
            {myGroupStats && myGroupStats.totalMatches > 0 && (
              <div style={{
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: 12,
                padding: 16,
                marginBottom: 16,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.textSecondary, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Mis stats en el grupo
                </div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 80, textAlign: 'center', background: theme.colors.background, borderRadius: 8, padding: '10px 8px' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: theme.colors.primary }}>{myGroupStats.attendancePercentage}%</div>
                    <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>Asistencia</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 80, textAlign: 'center', background: theme.colors.background, borderRadius: 8, padding: '10px 8px' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: theme.colors.text }}>{myGroupStats.currentStreak}</div>
                    <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>Racha actual</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 80, textAlign: 'center', background: theme.colors.background, borderRadius: 8, padding: '10px 8px' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: theme.colors.text }}>{myGroupStats.matchesAttended}/{myGroupStats.totalMatches}</div>
                    <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>Partidos</div>
                  </div>
                </div>
                {myGroupStats.matchHistory.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                    {myGroupStats.matchHistory.slice(-10).map((m) => (
                      <div key={m.matchId} style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: m.attended ? theme.colors.primary : theme.colors.border,
                      }} />
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 12, color: myGroupStats.isAboveAverage ? theme.colors.primary : theme.colors.textSecondary }}>
                  {myGroupStats.isAboveAverage ? '↑ Estás por encima del promedio del grupo' : 'Por debajo del promedio del grupo'}
                </div>
              </div>
            )}
            {myGroupStatsLoading && (
              <p style={{ fontSize: 13, color: theme.colors.textSecondary, margin: '0 0 12px 0' }}>Cargando tus stats...</p>
            )}
            <GroupLeaderboard groupId={id} />
          </>
        )}

        {activeTab === 'seasons' && id && (
          <SeasonStandings groupId={id} isAdmin={group.userRole === 'ADMIN'} />
        )}

        {activeTab === 'chat' && id && (
          <GroupChat
            groupId={id}
            currentUserId={authUser?.id}
            isAdmin={group.userRole === 'ADMIN'}
            active={activeTab === 'chat'}
            polls={polls}
            votingPollId={votingPollId}
            onVotePoll={votePoll}
            onClosePoll={closePoll}
            onDeletePoll={deletePoll}
            onCreatePoll={() => setShowCreatePollModal(true)}
          />
        )}

        {activeTab === 'info' && (
          <>
            {/* Preferencia de notificaciones del grupo (por miembro) */}
            <button
              type="button"
              onClick={toggleGroupNotif}
              disabled={groupNotifSaving}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                padding: '12px 14px', marginBottom: 14, borderRadius: 12,
                border: `1px solid ${theme.colors.border}`, background: theme.colors.background,
                cursor: groupNotifSaving ? 'not-allowed' : 'pointer',
              }}
            >
              <span style={{ fontSize: 18 }}>🔔</span>
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', color: theme.colors.text, fontSize: 14, fontWeight: 600 }}>
                  Notificaciones de partidos del grupo
                </span>
                <span style={{ display: 'block', color: theme.colors.textSecondary, fontSize: 12 }}>
                  Avisos cuando se crea un partido nuevo en este grupo.
                </span>
              </span>
              <span style={{
                width: 44, height: 26, borderRadius: 13, flexShrink: 0, position: 'relative',
                background: groupNotifEnabled ? theme.colors.primary : theme.colors.border,
                transition: 'background 0.2s',
              }}>
                <span style={{
                  position: 'absolute', top: 3, left: groupNotifEnabled ? 21 : 3,
                  width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'left 0.2s',
                }} />
              </span>
            </button>

            {groupStatsLoading ? (
              <p style={{ color: theme.colors.textSecondary, margin: 0 }}>Cargando estadísticas...</p>
            ) : !groupStats ? (
              <p style={{ color: theme.colors.textSecondary, margin: 0 }}>No hay estadísticas disponibles.</p>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
                  <div style={{ padding: 12, borderRadius: 10, background: theme.colors.background, border: `1px solid ${theme.colors.border}` }}>
                    <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>Miembros</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text }}>{groupStats.groupInfo.totalMembers}</div>
                  </div>
                  <div style={{ padding: 12, borderRadius: 10, background: theme.colors.background, border: `1px solid ${theme.colors.border}` }}>
                    <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>Partidos</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text }}>{groupStats.groupInfo.totalMatches}</div>
                  </div>
                  <div style={{ padding: 12, borderRadius: 10, background: theme.colors.background, border: `1px solid ${theme.colors.border}` }}>
                    <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>Próximos</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text }}>{groupStats.groupInfo.upcomingMatches}</div>
                  </div>
                  <div style={{ padding: 12, borderRadius: 10, background: theme.colors.background, border: `1px solid ${theme.colors.border}` }}>
                    <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>Creado</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>{formatDateTime(groupStats.groupInfo.createdAt)}</div>
                  </div>
                </div>

                <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: theme.colors.primary }}>Top asistencia</h4>
                <ul style={{ margin: '0 0 12px 18px', color: theme.colors.textSecondary }}>
                  {groupStats.topAttendance.slice(0, 5).map((m) => (
                    <li key={m.id}>{m.name}: {m.attendance}% ({m.playedMatches} PJ)</li>
                  ))}
                </ul>

                <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: theme.colors.warning }}>Top goleadores</h4>
                <ul style={{ margin: '0 0 12px 18px', color: theme.colors.textSecondary }}>
                  {groupStats.topScorers.slice(0, 5).map((m) => (
                    <li key={m.id}>{m.name}: {m.totalGoals} goles</li>
                  ))}
                </ul>

                <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#C4B5FD' }}>Rachas</h4>
                <ul style={{ margin: '0 0 12px 18px', color: theme.colors.textSecondary }}>
                  {groupStats.topStreaks.slice(0, 5).map((m) => (
                    <li key={m.id}>{m.name}: {m.currentStreak} seguidos</li>
                  ))}
                </ul>

                {groupStats.mvp && (
                  <p style={{ margin: 0, color: theme.colors.text }}>
                    🏆 MVP: <strong>{groupStats.mvp.name}</strong> ({groupStats.mvp.rating?.toFixed(1) ?? '-'})
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>

      {showCreateRecurringModal && id && group && (
        <CreateRecurringMatchModal
          groupId={id}
          groupName={group.name}
          onClose={() => setShowCreateRecurringModal(false)}
          onSuccess={() => {
            setShowCreateRecurringModal(false);
            fetchRecurringMatch();
          }}
          showNotification={showNotification}
        />
      )}

      {id && (
        <CreatePollModal
          isOpen={showCreatePollModal}
          groupId={id}
          upcomingMatches={upcomingMatches.map((m) => ({ id: m.id, location: m.location, date: m.date, time: m.time }))}
          onClose={() => setShowCreatePollModal(false)}
          onCreated={fetchPolls}
          showNotification={showNotification}
        />
      )}

      {id && (
        <AddMatchesFromHistoryModal
          isOpen={showAddMatchesModal}
          groupId={id}
          onClose={() => setShowAddMatchesModal(false)}
          onAdded={(count) => {
            showNotification(
              count > 0 ? `${count} partido${count === 1 ? '' : 's'} agregado${count === 1 ? '' : 's'} al grupo` : 'Listo',
              'success'
            );
            fetchGroupMatches();
          }}
        />
      )}

      {showAnnouncementModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 16 }}
          onClick={() => !announcementSending && setShowAnnouncementModal(false)}
          onKeyDown={(e) => { if (e.key === 'Escape' && !announcementSending) setShowAnnouncementModal(false); }}
          role="dialog"
          aria-modal="true"
        >
          <div style={{ background: theme.colors.surface, borderRadius: 16, padding: 24, maxWidth: 520, width: '100%', border: `1px solid ${theme.colors.border}` }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18, color: theme.colors.text }}>Enviar anuncio al grupo</h3>
            <textarea
              value={announcementMessage}
              onChange={(e) => setAnnouncementMessage(e.target.value)}
              maxLength={500}
              placeholder="Escribí un anuncio para los miembros del grupo..."
              style={{ width: '100%', minHeight: 120, resize: 'vertical', padding: 12, borderRadius: 10, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text, fontSize: 14, boxSizing: 'border-box' }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: theme.colors.textSecondary, textAlign: 'right' }}>{announcementMessage.length}/500</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => setShowAnnouncementModal(false)} disabled={announcementSending} style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${theme.colors.border}`, background: 'transparent', color: theme.colors.textSecondary, cursor: announcementSending ? 'not-allowed' : 'pointer' }}>
                Cancelar
              </button>
              <button type="button" onClick={sendAnnouncement} disabled={announcementSending || !announcementMessage.trim()} style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: announcementSending || !announcementMessage.trim() ? theme.colors.textSecondary : theme.colors.primary, color: theme.colors.background, fontWeight: 600, cursor: announcementSending || !announcementMessage.trim() ? 'not-allowed' : 'pointer' }}>
                {announcementSending ? 'Enviando...' : 'Enviar anuncio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditGroupModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 16 }}
          onClick={() => !editGroupSubmitting && setShowEditGroupModal(false)}
          onKeyDown={(e) => { if (e.key === 'Escape' && !editGroupSubmitting) setShowEditGroupModal(false); }}
          role="dialog"
          aria-modal="true"
        >
          <div style={{ background: theme.colors.surface, borderRadius: 16, padding: 24, maxWidth: 520, width: '100%', border: `1px solid ${theme.colors.border}` }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18, color: theme.colors.text }}>Editar grupo</h3>
            <input
              value={editGroupName}
              onChange={(e) => setEditGroupName(e.target.value)}
              placeholder="Nombre del grupo"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text, marginBottom: 10, boxSizing: 'border-box' }}
            />
            <textarea
              value={editGroupDescription}
              onChange={(e) => setEditGroupDescription(e.target.value)}
              placeholder="Descripción"
              style={{ width: '100%', minHeight: 90, resize: 'vertical', padding: 12, borderRadius: 10, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => setShowEditGroupModal(false)} disabled={editGroupSubmitting} style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${theme.colors.border}`, background: 'transparent', color: theme.colors.textSecondary, cursor: editGroupSubmitting ? 'not-allowed' : 'pointer' }}>
                Cancelar
              </button>
              <button type="button" onClick={updateGroup} disabled={editGroupSubmitting || !editGroupName.trim()} style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: editGroupSubmitting || !editGroupName.trim() ? theme.colors.textSecondary : theme.colors.primary, color: theme.colors.background, fontWeight: 600, cursor: editGroupSubmitting || !editGroupName.trim() ? 'not-allowed' : 'pointer' }}>
                {editGroupSubmitting ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddMembersModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          onClick={() => !addMembersSubmitting && setShowAddMembersModal(false)}
          onKeyDown={(e) => { if (e.key === 'Escape' && !addMembersSubmitting) setShowAddMembersModal(false); }}
          role="dialog"
          aria-modal="true"
        >
          <div style={{ background: theme.colors.surface, borderRadius: 16, padding: 24, maxWidth: 440, width: '100%', maxHeight: '85vh', overflow: 'auto', border: `1px solid ${theme.colors.border}` }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, color: theme.colors.text }}>Invitar al grupo</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                type="text"
                value={addMembersQuery}
                onChange={(e) => setAddMembersQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                placeholder="Buscar por nombre..."
                style={{ flex: 1, padding: '10px 12px', border: `2px solid ${theme.colors.border}`, borderRadius: 10, background: theme.colors.background, color: theme.colors.text, fontSize: 14 }}
              />
              <button type="button" onClick={searchUsers} disabled={addMembersSearching} style={{ padding: '10px 16px', background: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: 10, fontWeight: 600, cursor: addMembersSearching ? 'not-allowed' : 'pointer' }}>
                {addMembersSearching ? '...' : 'Buscar'}
              </button>
            </div>
            <div style={{ maxHeight: 240, overflow: 'auto', marginBottom: 16 }}>
              {searchUsersFiltered.length === 0 ? (
                <p style={{ color: theme.colors.textSecondary, fontSize: 14, margin: 0 }}>
                  {addMembersResults.length === 0 ? 'Buscá por nombre (mín. 2 letras).' : 'Ya son miembros o no hay resultados.'}
                </p>
              ) : (
                searchUsersFiltered.map((u) => (
                  <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer', borderBottom: `1px solid ${theme.colors.border}` }}>
                    <input type="checkbox" checked={addMembersSelected.has(u.id)} onChange={() => toggleAddMember(u.id)} />
                    <span style={{ color: theme.colors.text, fontWeight: 500 }}>{u.name}</span>
                    <span style={{ color: theme.colors.textSecondary, fontSize: 13 }}>⭐ {Number(u.rating).toFixed(1)}</span>
                  </label>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowAddMembersModal(false)} disabled={addMembersSubmitting} style={{ padding: '10px 16px', background: 'transparent', color: theme.colors.textSecondary, border: `1px solid ${theme.colors.border}`, borderRadius: 10, cursor: addMembersSubmitting ? 'not-allowed' : 'pointer' }}>
                Cancelar
              </button>
              <button type="button" onClick={inviteToGroup} disabled={addMembersSubmitting || addMembersSelected.size === 0} style={{ padding: '10px 16px', background: addMembersSelected.size === 0 || addMembersSubmitting ? theme.colors.textSecondary : theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: 10, fontWeight: 600, cursor: addMembersSelected.size === 0 || addMembersSubmitting ? 'not-allowed' : 'pointer' }}>
                {addMembersSubmitting ? 'Enviando...' : `Enviar invitación (${addMembersSelected.size})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1001, padding: '0 0 0 0' }}
          onClick={() => setShowShareModal(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowShareModal(false); }}
          role="dialog"
          aria-modal="true"
        >
          <div
            style={{
              background: theme.colors.surface,
              borderRadius: isMobile ? '20px 20px 0 0' : 20,
              padding: isMobile ? '24px 20px 36px' : 32,
              maxWidth: 520,
              width: '100%',
              border: `1px solid ${theme.colors.border}`,
              boxShadow: '0 -4px 40px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <h3 style={{ margin: '0 0 6px 0', fontSize: 18, color: theme.colors.text }}>
              🔗 Compartir grupo
            </h3>
            <p style={{ margin: '0 0 20px 0', color: theme.colors.textSecondary, fontSize: 14 }}>
              Cualquiera con este link puede unirse al grupo.
            </p>

            {/* Link box */}
            <div style={{
              background: theme.colors.background,
              borderRadius: 12,
              padding: '12px 14px',
              border: `1px solid ${theme.colors.border}`,
              marginBottom: 16,
              wordBreak: 'break-all',
              fontSize: 13,
              color: theme.colors.textSecondary
            }}>
              {inviteLink}
            </div>

            {/* Acciones */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                type="button"
                onClick={shareToWhatsApp}
                style={{
                  padding: '14px',
                  background: '#25D366',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
              >
                <span>💬</span> Compartir en WhatsApp
              </button>
              <button
                type="button"
                onClick={copyInviteLink}
                style={{
                  padding: '14px',
                  background: linkCopied ? 'rgba(0,230,118,0.15)' : theme.colors.background,
                  color: linkCopied ? theme.colors.primary : theme.colors.text,
                  border: `1px solid ${linkCopied ? theme.colors.primary : theme.colors.border}`,
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {linkCopied ? '✅ Link copiado' : '📋 Copiar link'}
              </button>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                style={{
                  padding: '12px',
                  background: 'transparent',
                  color: theme.colors.textSecondary,
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <PlayerPickerModal
        open={h2hPickerOpen}
        members={(group?.members ?? []).map((m) => ({
          id: m.id,
          name: m.name,
          nickname: null,
          avatar: m.avatar ?? null,
        }))}
        onClose={() => setH2hPickerOpen(false)}
        onConfirm={(a, b) => {
          setH2hPickerOpen(false);
          navigate(`/groups/${id}/h2h?a=${a}&b=${b}`);
        }}
      />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

    </div>
  );
};

export default GroupDetailPage;
